// @flow
import React, { Component } from 'react';
import _ from 'lodash';
import { Grid, List, Label } from 'semantic-grab-ui/src';

import type { CampaignElements, TridentCampaignDetails, TimeEvent, TimeEventMeta } from 'types/trident-campaigns';
import { timeEventFrequencyMap } from 'data/trident-campaigns';

import { getHedwigTemplateUrl, getSegmentUrl } from './campaign-analysis';
import './ElementBreakdownReview.scss';
import SegmentDetailDisplay from './SegmentDetailDisplay';

type Props = {
  elements: CampaignElements,
  campaign: TridentCampaignDetails,
};

// ElementBreakdownReview shows the elements used in a campaign: event types, templates, segments etc.
export default class ElementBreakdownReview extends Component {
  props: Props;

  shouldHighlightAction = (action: string) => {
    return _.includes(_.toLower(action), 'award');
  };

  getTimeEventDesc = (d: TimeEventMeta) => {
    const timeFormat = 'MMM Do YYYY, h:mm:ss a';
    if (d.frequency === 'nr') {
      return <span><b>{d.startTime.format(timeFormat)}</b>  (Once)</span>;
    }
    const interval = _.get(d, 'interval', 1);
    const freq = _.get(timeEventFrequencyMap, d.frequency, '');
    const repeat = interval > 1 ? `every ${interval} ${freq}s` : `every ${freq}`;
    return <span><b>{d.startTime.format(timeFormat)}</b> {repeat} until <b>{d.endTime.format(timeFormat)}</b></span>;
  };

  getTimeEventDisplay = (t: TimeEvent) => {
    if (_.isEmpty(t)) {
      return <div />;
    }
    const count = t.count > 1 ? <Label circular color="grey">{t.count}</Label> : null;
    return (
      <div>
        {count}
        {this.getTimeEventDesc(t.meta)}
      </div>
    );
  };

  getActionDisplay = (a: string) => {
    return <span className={this.shouldHighlightAction(a) ? 'highlighted-action' : ''}>{a}</span>;
  };

  getTemplateDisplay = (t: Object) => {
    const url = getHedwigTemplateUrl(t.type, t.id);
    return <a href={url} target="_blank">{url}</a>;
  };

  getSimpleSegmentDisplay = (s: string, targetUserType: string) => {
    return <a href={getSegmentUrl(targetUserType, s)} target="_blank">{s}</a>;
  };

  getDetailedSegmentDisplay = (s: string, targetUserType: string) => {
    const { campaign } = this.props;
    return (
      <SegmentDetailDisplay
        segment={s}
        targetUserType={targetUserType}
        countryID={_.get(campaign, 'countryID')}
      />
    );
  };

  getSimpleListView = (items: Array, horizontal: boolean) => {
    return _.isEmpty(items) ? null : (
      <List bulleted horizontal={horizontal}>
        {
          _.map(items, (e, index) => {
            return <List.Item key={index}>{e}</List.Item>;
          })
        }
      </List>
    );
  };

  render() {
    const targetUserType = _.get(this.props, 'campaign.targetUserType', '');
    const data = {
      'Event Types': {
        items: this.props.elements.eventTypes,
        horizontal: true,
      },
      'Time Events': { items: _.map(this.props.elements.timeEvents, t => this.getTimeEventDisplay(t)) },
      'Action Types': {
        items: _.map(this.props.elements.actionTypes, a => this.getActionDisplay(a)),
        horizontal: true,
      },
      Templates: { items: _.map(this.props.elements.templates, t => this.getTemplateDisplay(t)) },
      Rewards: {
        items: this.props.elements.rewardIDs,
        horizontal: true,
      },
      'Segments (Bulk Action)': {
        items: _.map(this.props.elements.bulkActionSegments, s => this.getDetailedSegmentDisplay(s, targetUserType)),
        horizontal: false,
      },
      'Segments (Update)': {
        items: _.map(this.props.elements.updateTargetSegments, s => this.getSimpleSegmentDisplay(s, targetUserType)),
        horizontal: true,
      },
    };

    return (
      <Grid>
        {
          _.map(data, (view, key) => {
            return !_.isEmpty(view.items) && (
              <Grid.Row key={key}>
                <Grid.Column width={3}>
                  <b>{key}:</b>
                </Grid.Column>
                <Grid.Column width={13}>
                  {this.getSimpleListView(view.items, view.horizontal)}
                </Grid.Column>
              </Grid.Row>
            );
          })
        }
      </Grid>
    );
  }
}
